import React, { useState } from 'react';
import { Button, Checkbox, Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ISignInChallenge } from '../../../global/entities/Login/SignInChallenge.ts';
import { CurrentUser } from '../../../global/entities/Login/CurrentUser.ts';
import { useVerifySignInCodeMutation } from '../../../global/entities/Login/mutations/useVerifySignInCodeMutation.tsx';
import { useSendSignInCodeMutation } from '../../../global/entities/Login/mutations/useSendSignInCodeMutation.tsx';
import { signInCodeMessage } from './signInCodeMessage.ts';

type SignInCodeStepProps = {
    challenge: ISignInChallenge;
    onSignedIn: (user: CurrentUser) => void;
    /** The sign-in itself failed (unknown, used or expired): the HCP starts over with their password. */
    onSignInFailed: (error: unknown) => void;
};

export const SignInCodeStep: React.FC<SignInCodeStepProps> = ({ challenge, onSignedIn, onSignInFailed }) => {
    const { t } = useTranslation();
    const verifyCode = useVerifySignInCodeMutation();
    const sendCode = useSendSignInCodeMutation();
    const [code, setCode] = useState('');
    const [trustBrowser, setTrustBrowser] = useState(false);
    const [lastError, setLastError] = useState<unknown>(null);

    const handleError = (error: unknown) => {
        if (signInCodeMessage(error, t)) setLastError(error);
        else onSignInFailed(error);
    };

    const handleVerifyCode = async () => {
        setLastError(null);
        try {
            onSignedIn(await verifyCode.mutateAsync({ signInToken: challenge.signInToken, code, trustBrowser }));
        } catch (error) {
            handleError(error);
        }
    };

    const handleSendCode = async () => {
        setLastError(null);
        try {
            await sendCode.mutateAsync({ signInToken: challenge.signInToken });
        } catch (error) {
            handleError(error);
        }
    };

    const message = signInCodeMessage(lastError, t);

    return (
        <Stack w="100%" gap="md">
            <Text>{t('activateAccount.codeScreen', { last4: challenge.mobileLast4 })}</Text>
            <TextInput
                label={t('activateAccount.code')}
                value={code}
                inputMode="numeric"
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, ''))}
            />
            <Checkbox
                label={t('signInCode.trustThisBrowser')}
                checked={trustBrowser}
                onChange={(e) => setTrustBrowser(e.currentTarget.checked)}
            />
            <Button loading={verifyCode.isPending} onClick={handleVerifyCode}>
                {t('activateAccount.continue')}
            </Button>
            <Button variant="subtle" loading={sendCode.isPending} onClick={handleSendCode}>
                {t('activateAccount.sendNewCode')}
            </Button>
            {message && (
                <Text size="sm" c="red" role="alert">
                    {message}
                </Text>
            )}
        </Stack>
    );
};
